import {z} from 'zod';
import dedent from 'dedent';
import {defineToolRegistryManifest} from '../../../types/base-tool-types';

const toolName = 'seo-audit-meta-field-freshness' as const;

export const freshnessAuditItemSchema = z.object({
  pageId: z.string(),
  seoTitleFresh: z
    .boolean()
    .optional()
    .describe('Whether the SEO title is fresh relative to the page content'),
  seoDescFresh: z
    .boolean()
    .optional()
    .describe(
      'Whether the meta description is fresh relative to the page content'
    ),
  jsonLdSchemaFresh: z
    .boolean()
    .optional()
    .describe(
      'Whether the JSON-LD schema is fresh relative to the page content'
    ),
  error: z
    .string()
    .optional()
    .describe('Error message if freshness check failed for this page'),
});

export type FreshnessAuditItem = z.infer<typeof freshnessAuditItemSchema>;

export const auditMetaFieldFreshnessArgsSchema = z.object({
  siteId: z.string().describe('The ID of the site'),
  pageId: z.string().describe('The ID of the page to check for freshness'),
});

export type AuditMetaFieldFreshnessArgs = z.infer<
  typeof auditMetaFieldFreshnessArgsSchema
>;

export const auditMetaFieldFreshnessResultSchema = freshnessAuditItemSchema;

export type AuditMetaFieldFreshnessResult = z.infer<
  typeof auditMetaFieldFreshnessResultSchema
>;

export const auditMetaFieldFreshnessManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Audit meta field freshness',
  prettyStatus: {
    executed: 'Audited meta field freshness',
    executing: 'Auditing meta field freshness',
    failed: 'Failed to audit meta field freshness',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Checks whether a single page's existing meta fields (SEO title, meta
      description, JSON-LD schema) are still fresh — i.e., accurately reflect
      the current page content.

      Returns per-field freshness booleans indicating whether each meta field
      is still aligned with what the page actually contains.
    `,
    inputSchema: auditMetaFieldFreshnessArgsSchema,
  },
  type: 'server',
  permission: [
    {
      resource: 'SitePageSettings',
      action: 'read',
      resolveResources: (args) => [{type: 'SitePageSettings', id: args.pageId}],
    },
  ],
  confirmBeforeExecuting: false,
  timeout: 120000,
});
